/*
  © 2023 KondaSoft
  https://www.kondasoft.com
*/

/**
 * @typedef {import("swiper/types").Swiper} Swiper
 * @typedef {import("swiper/types").SwiperOptions} SwiperOptions
*/

class ProductCardImageGallery extends HTMLElement {
  constructor () {
    super()

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper'), this.splideOptions())
  }

  splideOptions () {
    /** @type {SwiperOptions} */
    const options = {
      rewind: true,
      speed: 300,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      }
    }
    return options
  }
}
customElements.define('product-card-image-gallery', ProductCardImageGallery)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide)

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.listenVariantChange()
    this.initFullScreenGallery()
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      effect: this.dataset.effect,
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: this.dataset.paginationType,
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      },
      cubeEffect: {
        shadow: false
      },
      creativeEffect: {
        prev: {
          shadow: true,
          origin: 'left center',
          translate: ['-5%', 0, -200],
          rotate: [0, 100, 0]
        },
        next: {
          origin: 'right center',
          translate: ['5%', 0, -200],
          rotate: [0, -100, 0]
        }
      },
      scrollbar: {
        enabled: this.dataset.scrollbar === 'true',
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true,
        hide: true
      }
    }
    return options
  }

  setStickyPosition () {
    if (this.closest('.product-template') && window.matchMedia('(min-width: 992px)').matches) {
      const navbarHeight = document.querySelector('[id*="__navbar"].sticky-top')?.clientHeight || 0
      this.style.top = `${navbarHeight + 20}px`
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }

  initFullScreenGallery () {
    if (!this.dataset.vendorGlightboxJs) return

    const selector = `[data-gallery="gallery-${this.dataset.productId}"]`
    let lightbox = null
    let loading = null

    const load = () => {
      if (loading) return loading

      loading = new Promise(resolve => {
        const vendorStyle = document.createElement('link')
        vendorStyle.rel = 'stylesheet'
        vendorStyle.href = this.dataset.vendorGlightboxCss
        document.head.appendChild(vendorStyle)

        const vendorScript = document.createElement('script')
        vendorScript.src = this.dataset.vendorGlightboxJs
        vendorScript.onload = () => {
          lightbox = window.GLightbox({
            selector,
            loop: true,
            openEffect: 'zoom',
            closeEffect: 'fade',
            slideEffect: this.dataset.fullScreenEffect
          })
          resolve(lightbox)
        }
        document.head.appendChild(vendorScript)
      })

      return loading
    }

    // Load quietly once the page is idle, so it never competes with the first paint
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 3000))
    window.addEventListener('load', () => idle(load, { timeout: 5000 }), { once: true })

    // If someone taps a photo before it has loaded, load it and then open that photo
    this.addEventListener('click', async (event) => {
      if (lightbox) return

      const link = event.target.closest(selector)
      if (!link) return

      event.preventDefault()
      const instance = await load()
      const index = [...this.querySelectorAll(selector)].indexOf(link)
      instance.openAt(Math.max(index, 0))
    })
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)
